/**
 * Позначки: індекс у пам'яті, суми компенсації, застосування тижневих шаблонів.
 * Модуль чистий — однаково працює на сервері, у боті й у браузері.
 */
import { defaultTemplate } from './defaults.js';
import { addDays, rangeDates, weekIndex } from './dates.js';
import { MARK_LAYERS } from './types.js';
export const cellKey = (date, meal) => `${date}#${meal}`;
export class MarkIndex {
    byPerson = new Map();
    static from(marks) {
        const ix = new MarkIndex();
        for (const m of marks)
            ix.set(m.personId, m.date, m.meal, m.kind, m.layer);
        return ix;
    }
    slot(personId) {
        let p = this.byPerson.get(personId);
        if (!p) {
            p = { main: new Map(), hint: new Map(), duty: new Map(), away: new Map() };
            this.byPerson.set(personId, p);
        }
        return p;
    }
    person(personId) {
        return this.byPerson.get(personId);
    }
    get(personId, date, meal, layer = 'main') {
        return this.byPerson.get(personId)?.[layer].get(cellKey(date, meal)) ?? null;
    }
    set(personId, date, meal, kind, layer = 'main') {
        const slot = this.slot(personId);
        const key = cellKey(date, meal);
        if (kind)
            slot[layer].set(key, kind);
        else
            slot[layer].delete(key);
    }
    apply(op) {
        this.set(op.personId, op.date, op.meal, op.kind, op.layer ?? 'main');
    }
    applyAll(ops) {
        for (const op of ops)
            this.apply(op);
    }
    removePerson(personId) {
        this.byPerson.delete(personId);
    }
    *all() {
        for (const [personId, slot] of this.byPerson) {
            for (const layer of MARK_LAYERS) {
                for (const [key, kind] of slot[layer]) {
                    const [date, meal] = key.split('#');
                    yield { personId, date: date, meal: Number(meal), kind, layer };
                }
            }
        }
    }
    get size() {
        let n = 0;
        for (const slot of this.byPerson.values()) {
            for (const layer of MARK_LAYERS)
                n += slot[layer].size;
        }
        return n;
    }
}
/* -------------------------------------------------------------------- суми */
/**
 * Пошук типу позначки за кодом.
 *
 * Через цю функцію проходить кожна клітинка таблиці, а типів у налаштуваннях
 * два десятки — перебором це виходило під мільйон порівнянь на один
 * перемальований місяць. Тому тримаємо покажчик на сам масив: він приходить
 * із налаштуваннями й не міняється на місці, тож кеш живе рівно стільки,
 * скільки живуть налаштування.
 */
const typeIndexes = new WeakMap();
export function markTypeOf(types, code) {
    let ix = typeIndexes.get(types);
    if (!ix) {
        ix = new Map(types.map(t => [t.code, t]));
        typeIndexes.set(types, ix);
    }
    return ix.get(code);
}
export const isCounted = (types, code) => !!code && !!markTypeOf(types, code)?.counted;
/** Скільки клітинок, що рахуються, стоїть у людини за проміжок. */
export function countedCells(marks, dates, mealCount, types) {
    if (!marks)
        return 0;
    let n = 0;
    for (const d of dates) {
        for (let m = 0; m < mealCount; m++) {
            if (isCounted(types, mealKindAt(marks, d, m)))
                n++;
        }
    }
    return n;
}
/* ---------------------------------------------------------------- шаблони */
/**
 * Який тижневий розклад діє для людини:
 * власний шаблон → призначена роль → роль за замовчуванням → лише вихідні.
 */
export function templateOf(person, mealCount, weekStart, patterns = []) {
    if (person.template && person.template.length === 7)
        return person.template;
    const assigned = person.patternId ? patterns.find(p => p.id === person.patternId) : undefined;
    const fallback = assigned ?? patterns.find(p => p.isDefault);
    if (fallback?.days?.length === 7)
        return fallback.days;
    return defaultTemplate(false, mealCount, weekStart);
}
export function templateWants(tpl, date, meal, weekStart) {
    return !!tpl[weekIndex(date, weekStart)]?.[meal];
}
/** Побудувати операції заповнення таблиці за тижневими шаблонами людей. */
export function fillFromTemplates(people, from, to, index, meals, weekStart, opt, patterns = []) {
    const ops = [];
    const dates = rangeDates(from, to);
    for (const p of people) {
        if (!p.active)
            continue;
        const tpl = templateOf(p, meals.length, weekStart, patterns);
        const pm = index.person(p.id);
        for (const d of dates) {
            for (let m = 0; m < meals.length; m++) {
                const want = templateWants(tpl, d, m, weekStart);
                const current = pm?.main.get(cellKey(d, m)) ?? null;
                if (current && !opt.overwrite)
                    continue;
                const next = want ? opt.kind : (opt.presentKind ?? null);
                if (next === current)
                    continue;
                ops.push({ personId: p.id, date: d, meal: m, kind: next, layer: 'main' });
            }
        }
    }
    return ops;
}
/**
 * Перенести наряди, відпустки й ЛР у таблицю харчування.
 *
 * Вони живуть у своїх таблицях і самі в харчування не потрапляють: діловод
 * переносить їх кнопкою, коли місяць розставлений. Клітинка отримує позначку
 * відсутності або наряду замість того, що там стояло; відсутність важить
 * більше — людини, якої немає, у наряд не ставлять. Якщо наряд чи відпустку
 * вже зняли, а позначка лишилася з минулого перенесення, клітинка
 * повертається до шаблону харчування людини — так само, як після «Заповнити».
 *
 * Наряди «лише для нарядів» (кібер наряд) харчування не займають і
 * не переносяться.
 *
 * `only` — які саме позначки переносити (діловод обирає їх у вікні
 * «Перенесення»). Решта лишається як є: і в харчуванні, і в своїх таблицях.
 *
 * `afterDuty` — дні добових рапортів. Добове тягнеться до ранку, сніданок
 * наступного дня людина пропускає — і за нього ставиться компенсація, якщо
 * клітинка порожня й нічого іншого туди не переноситься.
 *
 * `mealsOf` — для позначки можна обрати лише частину прийомів їжі: решта
 * клітинок цієї позначки не переноситься й лишається в харчуванні як є.
 */
export function transferIntoMealsOps(people, dates, index, settings, only, afterDuty = [], mealsOf) {
    const types = settings.markTypes;
    const picked = new Set(transferableKindsOf(settings).filter(k => !only || only.has(k)));
    const brings = (kind, meal) => !!kind && picked.has(kind) && (mealsOf?.get(kind)?.has(meal) ?? true);
    const counted = types.find(t => t.counted && !t.dutyOnly)?.code ?? null;
    const mornings = new Set(afterDuty.map(r => `${r.personId}|${addDays(r.date, 1)}`));
    const ops = [];
    for (const p of people) {
        if (!p.active)
            continue;
        // Позначок у людини може не бути зовсім — компенсація після добового
        // однаково лягає в порожню клітинку.
        const pm = index.person(p.id);
        const tpl = templateOf(p, settings.meals.length, settings.weekStart, settings.mealPatterns);
        for (const d of dates) {
            for (let m = 0; m < settings.meals.length; m++) {
                const key = cellKey(d, m);
                const away = pm?.away.get(key) ?? null;
                const duty = pm?.duty.get(key) ?? null;
                const source = (brings(away, m) ? away : null) ?? (brings(duty, m) ? duty : null);
                const meal = pm?.main.get(key) ?? null;
                let next = meal;
                if (source)
                    next = source;
                else if (brings(meal, m)) {
                    next = templateWants(tpl, d, m, settings.weekStart) ? counted : null;
                }
                if (m === 0 && !next && mornings.has(`${p.id}|${d}`))
                    next = counted;
                if (next !== meal)
                    ops.push({ personId: p.id, date: d, meal: m, kind: next, layer: 'main' });
            }
        }
    }
    return ops;
}
/**
 * Що взагалі можна перенести в харчування: наряди, що займають прийом їжі, і
 * відсутність. Порядок — як у налаштуваннях позначок.
 */
/** Нове правило керує перенесенням, а не видимістю вже записаних клітинок. */
export const visibleInMeals = (t) => t.transferToMeals !== undefined || isAwayType(t) || !t.dutyOnly;
export const allowsMealTransfer = (t) => t.transferToMeals ?? (isAwayType(t) || !t.dutyOnly);
export function transferableKindsOf(settings) {
    const duties = new Set(dutyKindsOf(settings));
    const away = new Set(awayKindsOf(settings.markTypes));
    return settings.markTypes
        .filter(t => (away.has(t.code) || duties.has(t.code)) && allowsMealTransfer(t))
        .map(t => t.code);
}
/** Відпустка — позначка статусу з причиною «відпустка» для рапорту. */
export const isVacationType = (t) => t.awayReason?.trim().toLowerCase() === 'відпустка';
/** Клітинки доби для позначки, що займає кілька прийомів їжі (наряд). */
/**
 * Чи це наряд.
 *
 * Явний прапорець, а для налаштувань, збережених до його появи, — усе, що
 * заповнює більше однієї клітинки. Правило одне на застосунок: воно вирішує і
 * склад палітри в таблиці нарядів, і те, які позначки означають «людини
 * немає», тож розходитись копіям тут нема де.
 */
export const isDutyType = (t) => t.duty ?? t.spread > 1;
/**
 * У якому шарі живе позначка — тобто в якій таблиці її ставлять.
 *
 * Наряд — у таблиці нарядів, відпустка й ЛР — у таблиці відпусток, решта — у
 * харчуванні. Окремий шар потрібен тому, що клітинка тримає одну позначку на
 * шар: у `main` наряд чи відпустка затирали б компенсацію на тому самому
 * прийомі їжі. У харчування вони потрапляють кнопкою перенесення.
 */
export const layerOfKind = (types, kind) => {
    const type = kind ? markTypeOf(types, kind) : undefined;
    if (type && isAwayType(type))
        return 'away';
    // Будь-який наряд — у власному шарі поверх харчування: компенсація під ним
    // лишається на місці, і коли наряд знімають чи переставляють, вона одразу
    // повертається в таблицю — заново її ставити не треба.
    return type && (type.dutyOnly || isDutyType(type)) ? 'duty' : 'main';
};
/**
 * Що стоїть у клітинці таблиці харчування — для неї самої й для всього обліку.
 *
 * Лише шар харчування. Наряди й відпустки поверх нього не накладаються:
 * діловод переносить їх сюди кнопкою (`transferIntoMealsOps`), і до того
 * таблиця харчування показує й рахує рівно те, що в ній стоїть.
 */
export function mealKindAt(marks, date, meal) {
    return marks?.main.get(cellKey(date, meal)) ?? null;
}
/**
 * Позначка наряду в клітинці — лише з таблиці нарядів.
 *
 * Перенесений наряд лежить і в харчуванні, але таблиця нарядів на нього не
 * дивиться: інакше знятий наряд лишався б у ній, доки його позначка стоїть у
 * харчуванні. Старі наряди, що жили в `main`, міграція скопіювала сюди.
 */
export const dutyAt = (marks, date, meal) => marks?.duty.get(cellKey(date, meal)) ?? null;
/**
 * Позначки, які вважаються нарядами: явний прапорець, а для старих
 * налаштувань — усе, що заповнює більше однієї клітинки, плюс те, що обране
 * в розборі нарядів.
 */
export function dutyKindsOf(settings) {
    // ЛР колись ставили серед нарядів, але це відсутність, а не наряд: він живе
    // в таблиці відпусток і в стовпчики нарядів не входить.
    const flagged = settings.markTypes
        .filter(t => isDutyType(t) && !isAwayType(t))
        .map(t => t.code);
    const fromDuties = [
        settings.duties.markKind,
        ...(settings.duties.sectionMarks ?? []).map(s => s.markKind),
    ];
    return [...new Set([...flagged, ...fromDuties].filter((k) => !!k))];
}
/**
 * Наряди, які знімають людину зі списку звільнення.
 *
 * Не кожен наряд тримає людину в розташуванні: кібер наряд несуть, не
 * втрачаючи звільнення, тож у списку така людина лишається звичайним рядком.
 * Які саме — вирішує ознака позначки, а не її код.
 */
export const leaveBlockingKindsOf = (settings) => dutyKindsOf(settings).filter(code => !markTypeOf(settings.markTypes, code)?.keepsLeave);
/**
 * Чи це відсутність — відпустка, ЛР, лікарняний: позначка таблиці відпусток.
 *
 * Явна відповідь — ознака `away`, її ставить вибір «де ставиться» в
 * налаштуваннях. Без неї вгадуємо: є причина для рапорту на винагороду
 * («відпустка», «хвороба») — так ЛР, який довго стояв серед нарядів, теж
 * відсутність; або позначка не рахується, не означає їдальню й не наряд.
 */
export const isAwayType = (t) => t.away ?? (!!t.awayReason?.trim() || (!t.counted && !t.eatsInCanteen && !isDutyType(t)));
/**
 * Чи їсть людина в їдальні з цією позначкою — не налаштування, а наслідок.
 * Компенсація — не їсть; статус о/с — людини немає взагалі; будь-що інше
 * (наряд, присутність) — їсть.
 */
export const eatsInCanteenOf = (t) => !t.counted && !isAwayType(t);
/** Чи згадується позначка в дужках розходу ВМЗ; без явного вибору — так. */
export const showsInExpense = (t) => t.inExpense ?? true;
/** Позначка з ознакою «їсть у їдальні», виставленою за правилом. */
export const withMealRule = (t) => ({ ...t, eatsInCanteen: eatsInCanteenOf(t) });
/** Позначки таблиці відпусток: ними закривають цілу добу. */
export const awayKindsOf = (types) => types.filter(isAwayType).map(t => t.code);
/** Відсутність на добу з таблиці відпусток, якщо вона стоїть. */
export function awayAt(marks, date, mealCount) {
    if (!marks)
        return null;
    for (let m = 0; m < mealCount; m++) {
        const kind = marks.away.get(cellKey(date, m));
        if (kind)
            return kind;
    }
    return null;
}
/**
 * Клітинки одного наряду навколо вказаного дня: доба починається з останнього
 * прийому їжі й переходить на наступний день, тож блок шукається в обидва боки.
 */
export function dutyBlock(marks, date, kind, mealCount) {
    if (!marks)
        return [];
    // Наряд знімається лише з таблиці нарядів: перенесена в харчування копія
    // лишається, доки діловод не перенесе наряди знову.
    const layerAt = (d, m) => marks.duty.get(cellKey(d, m)) === kind ? 'duty' : null;
    const has = (d, m) => layerAt(d, m) !== null;
    // Доба починається з останнього прийому їжі, тож у клікнутому дні позначка
    // може стояти на будь-якій клітинці — шукаємо, за що зачепитися.
    let anchor = -1;
    for (let m = 0; m < mealCount; m++) {
        if (has(date, m)) {
            anchor = m;
            break;
        }
    }
    if (anchor < 0)
        return [];
    let d = date;
    let m = anchor;
    // Крок назад, поки триває той самий наряд.
    for (;;) {
        const [pd, pm] = m === 0 ? [addDays(d, -1), mealCount - 1] : [d, m - 1];
        if (!has(pd, pm))
            break;
        d = pd;
        m = pm;
    }
    // І вперед, поки блок не урвався.
    const out = [];
    while (has(d, m)) {
        out.push({ date: d, meal: m, layer: layerAt(d, m) });
        if (m + 1 >= mealCount) {
            d = addDays(d, 1);
            m = 0;
        }
        else {
            m++;
        }
    }
    return out;
}
/**
 * З якого прийому їжі починається позначка.
 *
 * Наряд заступають увечері, тому типово це останній прийом дня — так само,
 * як його ставить клік у таблиці нарядів. Підрозділ, у якого зміна
 * відбувається зранку, задає свій прийом у налаштуваннях позначки.
 */
export const startMealOf = (type, mealCount) => {
    const wanted = type?.startMeal;
    return wanted !== undefined && wanted >= 0 && wanted < mealCount ? wanted : mealCount - 1;
};
/**
 * Скільки клітинок займає позначка в таблиці нарядів.
 *
 * Довжина в нарядах живе окремим полем: у таблиці харчування та сама позначка
 * може замальовувати зовсім іншу кількість клітинок, і правити одне число,
 * псуючи друге, — не діло. Старі налаштування окремого поля не мають, тож для
 * них лишається `spread`, а якщо немає й його — довжина, задана місцем виклику.
 */
export const dutySpreadOf = (type, fallback) => type?.dutySpread ?? type?.spread ?? fallback;
/**
 * Скільки клітинок харчування замалювати кліком.
 *
 * Поле «у харчуванні» і довжина наряду колись роз’їжджались: зміна четвірки
 * в харчуванні записувала стару трійку в наряд, і клік далі малював три
 * клітинки. Беремо більше з двох — щоб те, що поставили в позначках, не
 * програвало застарілому числу.
 */
export const paintSpreadOf = (type, fallback) => {
    const duty = dutySpreadOf(type, fallback);
    return Math.max(type?.spread ?? duty, duty);
};
/**
 * До чиєї колонки й з якою вагою йде наряд. Типово наряд рахується сам за
 * себе, але кібер наряд — це пів чергування, і в підсумку він додається до
 * звичайного, а не стоїть окремо.
 */
export function dutyCountOf(types, code) {
    const link = markTypeOf(types, code)?.dutyCountsAs;
    return link?.code ? { code: link.code, weight: link.weight || 1 } : { code, weight: 1 };
}
/** Стовпчики підрахунку нарядів: лише ті позначки, що рахуються самі за себе. */
export const dutyColumnsOf = (settings) => dutyKindsOf(settings).filter(code => dutyCountOf(settings.markTypes, code).code === code);
/**
 * Чи саме тут починається доба наряду.
 *
 * Рахувати наряди за останнім прийомом їжі не можна: заступають увечері не
 * всі — кібер наряд починається з обіду, а довгий наряд узагалі накриває
 * кілька вечер поспіль. Початок видно з сусідньої клітинки: якщо позаду вже
 * не той самий наряд, то доба почалася тут.
 */
export function dutyStartsAt(marks, date, meal, kind, mealCount) {
    const at = (d, m) => dutyAt(marks, d, m);
    if (at(date, meal) !== kind)
        return false;
    const [pd, pm] = meal === 0 ? [addDays(date, -1), mealCount - 1] : [date, meal - 1];
    return at(pd, pm) !== kind;
}
/**
 * Скільки нарядів у людини за проміжок — з тими самими вагами, що й у
 * стовпчиках: кожна доба рахується раз, а кібер наряд — половиною.
 */
export function dutyLoadOf(marks, dates, kinds, types, mealCount) {
    if (!marks)
        return 0;
    let n = 0;
    for (const d of dates) {
        for (let m = 0; m < mealCount; m++) {
            const kind = dutyAt(marks, d, m);
            if (!kind || !kinds.includes(kind))
                continue;
            if (dutyStartsAt(marks, d, m, kind, mealCount))
                n += dutyCountOf(types, kind).weight;
        }
    }
    return n;
}
/** «3» або «3,5»: пів наряду пишеться десятковою комою, як і в паперових звітах. */
export const dutyCountText = (n) => !n ? '' : Number.isInteger(n) ? String(n) : n.toFixed(1).replace('.', ',');
export function spreadCells(date, meal, spread, mealCount) {
    const out = [];
    let d = date;
    let m = meal;
    for (let i = 0; i < Math.max(1, spread); i++) {
        out.push({ date: d, meal: m });
        m++;
        if (m >= mealCount) {
            m = 0;
            d = addDays(d, 1);
        }
    }
    return out;
}
/**
 * Компенсація за шаблоном харчування людини, мінус наряди й мінус те, що вона
 * все-таки з'їла. Той самий розрахунок, що робив бот-попередник.
 *
 * Наряд забирає останній прийом їжі свого дня і всі, крім останнього,
 * наступного: доба наряду починається з вечері й тягнеться до обіду.
 */
export function compensationCells(person, input) {
    const { from, to, meals, weekStart } = input;
    const count = meals.length;
    const tpl = templateOf(person, count, weekStart, input.patterns ?? []);
    const wanted = new Set();
    for (const d of rangeDates(from, to)) {
        for (let m = 0; m < count; m++) {
            if (templateWants(tpl, d, m, weekStart))
                wanted.add(cellKey(d, m));
        }
    }
    const month = from.slice(0, 7);
    const dayIso = (day) => `${month}-${String(day).padStart(2, '0')}`;
    for (const day of input.duty) {
        const d = dayIso(day);
        wanted.delete(cellKey(d, count - 1));
        const next = addDays(d, 1);
        for (let m = 0; m < count - 1; m++)
            wanted.delete(cellKey(next, m));
    }
    for (const [day, list] of Object.entries(input.ate)) {
        for (const m of list)
            wanted.delete(cellKey(dayIso(Number(day)), m));
    }
    return [...wanted]
        .map(key => {
        const [date, meal] = key.split('#');
        return { date: date, meal: Number(meal) };
    })
        .sort((a, b) => (a.date === b.date ? a.meal - b.meal : a.date < b.date ? -1 : 1));
}
//# sourceMappingURL=marks.js.map